/* eslint-disable react/jsx-handler-names */
/* eslint-disable react/jsx-no-bind */
import React, { useRef, useEffect, useContext } from 'react';
import { withNav } from 'react-skeletal-nav';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { useBounds } from '../../../hooks';

import { CaretLeft } from '../../SVGs';
import TrustpilotWidget from '../../TrustpilotWidget';

import HeaderContext from '../context';

import './Nav.scss';

const isBrowser = typeof window !== 'undefined';

const targetIsOutside = (e, ref) => !(ref && ref.contains(e.target));

const isActuallyVisible = el => el.offsetParent === null;

const NavContent = ({ children, fullWidth, isScrolling, isVisible }) => {
  const ref = useRef();
  const { top = 0 } = useBounds(ref, [isVisible, isScrolling]);

  return (
    <div
      className={cx('nav__content', {
        'nav__content--full-width': fullWidth
      })}>
      <nav
        className="nav__content__inner"
        ref={ref}
        style={{
          maxHeight: `calc(100vh - ${top}px)`
        }}>
        {children}
      </nav>
    </div>
  );
};

NavContent.propTypes = {
  children: PropTypes.node.isRequired,
  fullWidth: PropTypes.bool,
  isScrolling: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired
};

const handleClickOutside = (e, ref, goBack) => {
  if (
    ref.current &&
    // Ensure click exists outside of ref
    targetIsOutside(e, ref.current) &&
    // Ensure click exists outside of ref's parent
    targetIsOutside(e, ref.current.parentElement) &&
    // Check if ref is visible in DOM. Prefer this to `isVisible` prop since
    // this is _still rendered_ on mobile, even if `isVisible` is false,
    // meaning this method still fires.
    !isActuallyVisible(ref.current)
  ) {
    goBack();
  }
};

const registerClickOutside = (ref, props) => {
  let listener;

  if (isBrowser && props.isInline) {
    listener = document.addEventListener('click', e =>
      handleClickOutside(e, ref, props.goBack)
    );
  }

  return () => listener && document.removeEventListener('click', listener);
};

const Nav = props => {
  const ref = useRef();
  const context = useContext(HeaderContext);
  const backButtonText = props.backButtonText || context.backButtonText || 'Back';

  useEffect(() => registerClickOutside(ref, props), [props.isVisible, ref]);

  const isRoot = props.route === 'root';

  return (
    <React.Fragment>
      <div
        className={cx('nav', {
          'nav--root': isRoot,
          'nav--child': !isRoot,
          'nav--inline': props.isInline,
          'nav--stack': !props.isInline,
          'nav--visible': props.isVisible,
          'nav--past': !props.isVisible && props.isPast
        })}
        ref={ref}>
        {!props.isInline && (
          <div className="nav__header">
            {!isRoot && (
              <div className="nav__back-button-wrapper">
                <button className="nav__back-button" onClick={props.goBack}>
                  <div className="nav__back-button__icon">
                    <CaretLeft />
                  </div>
                  {backButtonText}
                </button>
              </div>
            )}

            <div className="nav__title">{props.title}</div>

            {!isRoot && <div className="nav__spacer" />}
          </div>
        )}

        <NavContent
          fullWidth={props.fullWidth}
          isVisible={props.isVisible}
          isScrolling={context.isScrolling}>
          {props.children}

          {isRoot && props.includeTrustpilot && (
            <div className="nav__content__trustpilot">
              <TrustpilotWidget
                templateId="micro"
                isFlush
                isComponent={false}
              />
            </div>
          )}
        </NavContent>
      </div>
    </React.Fragment>
  );
};

Nav.propTypes = {
  children: PropTypes.node.isRequired,
  depth: PropTypes.number.isRequired,
  fullWidth: PropTypes.bool,
  goBack: PropTypes.func.isRequired,
  isInline: PropTypes.bool,
  isInRoute: PropTypes.bool,
  isPast: PropTypes.bool.isRequired,
  isStack: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  route: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  backButtonText: PropTypes.string,
  includeTrustpilot: PropTypes.bool
};

export default withNav(Nav);
